import {
  Alert,
  Box,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowForwardOutlined, ViewInArOutlined } from "@mui/icons-material";

type EquipmentLinkLike = Partial<{
  id: number | null;
  Id: number | null;
  containerId: string | null;
  ContainerId: string | null;
  navieraBookingId: number | null;
  NavieraBookingId: number | null;
  navieraBookingReference: string | null;
  NavieraBookingReference: string | null;
  repoDocBookingId: number | null;
  RepoDocBookingId: number | null;
  repoDocBookingReference: string | null;
  RepoDocBookingReference: string | null;
  navieraBookingEquipmentId: number | null;
  NavieraBookingEquipmentId: number | null;
  repoDocBookingEquipmentId: number | null;
  RepoDocBookingEquipmentId: number | null;
  navieraContainerId: string | null;
  NavieraContainerId: string | null;
  repoDocContainerId: string | null;
  RepoDocContainerId: string | null;
  navieraEquipmentCode: string | null;
  NavieraEquipmentCode: string | null;
  repoDocEquipmentCode: string | null;
  RepoDocEquipmentCode: string | null;
}>;

type BookingContext = {
  navieraBookingId?: number | null;
  navieraBookingReference?: string | null;
  repoDocBookingId?: number | null;
  repoDocBookingReference?: string | null;
};

interface EquipmentLinksSummaryProps {
  links: EquipmentLinkLike[];
  currentBookingId?: number | null;
  context?: BookingContext;
  title?: string;
  emptyMessage?: string;
  compact?: boolean;
}

function read<T>(source: EquipmentLinkLike, ...keys: string[]): T | null {
  const record = source as Record<string, unknown>;
  for (const key of keys) {
    const value = record[key];
    if (value !== undefined && value !== null) return value as T;
  }
  return null;
}

function fallback<T>(value: T | null | undefined, fallbackValue: T | null | undefined): T | null {
  return value ?? fallbackValue ?? null;
}

function bookingLabel(reference: string | null, id: number | null): string {
  if (reference && id) return `${reference} (#${id})`;
  if (reference) return reference;
  if (id) return `#${id}`;
  return "—";
}

function normalizeLink(link: EquipmentLinkLike, context?: BookingContext) {
  const navieraBookingId = fallback(
    read<number>(link, "navieraBookingId", "NavieraBookingId"),
    context?.navieraBookingId
  );
  const repoDocBookingId = fallback(
    read<number>(link, "repoDocBookingId", "RepoDocBookingId"),
    context?.repoDocBookingId
  );
  const navieraBookingReference = fallback(
    read<string>(link, "navieraBookingReference", "NavieraBookingReference"),
    context?.navieraBookingReference
  );
  const repoDocBookingReference = fallback(
    read<string>(link, "repoDocBookingReference", "RepoDocBookingReference"),
    context?.repoDocBookingReference
  );

  return {
    id: read<number>(link, "id", "Id"),
    containerId: read<string>(link, "containerId", "ContainerId"),
    navieraBookingId,
    navieraBookingReference,
    repoDocBookingId,
    repoDocBookingReference,
    navieraBookingEquipmentId: read<number>(link, "navieraBookingEquipmentId", "NavieraBookingEquipmentId"),
    repoDocBookingEquipmentId: read<number>(link, "repoDocBookingEquipmentId", "RepoDocBookingEquipmentId"),
    navieraContainerId: read<string>(link, "navieraContainerId", "NavieraContainerId") ?? read<string>(link, "containerId", "ContainerId"),
    repoDocContainerId: read<string>(link, "repoDocContainerId", "RepoDocContainerId") ?? read<string>(link, "containerId", "ContainerId"),
    navieraEquipmentCode: read<string>(link, "navieraEquipmentCode", "NavieraEquipmentCode"),
    repoDocEquipmentCode: read<string>(link, "repoDocEquipmentCode", "RepoDocEquipmentCode"),
  };
}

function EquipmentSideCard({
  label,
  source,
  booking,
  container,
  equipmentCode,
  equipmentId,
  accent,
  compact,
}: {
  label: string;
  source: "NAVIERA" | "REPO_DOC";
  booking: string;
  container: string | null;
  equipmentCode: string | null;
  equipmentId: number | null;
  accent: string;
  compact?: boolean;
}) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        border: "1px solid",
        borderColor: `${accent}33`,
        bgcolor: `${accent}08`,
        borderRadius: 1.5,
        px: compact ? 1 : 1.25,
        py: compact ? 0.75 : 1,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
        <Typography variant="caption" fontWeight={800} sx={{ color: accent, textTransform: "uppercase", letterSpacing: 0.4 }}>
          {label}
        </Typography>
        <Chip
          label={source}
          size="small"
          variant="outlined"
          sx={{ height: 18, fontSize: "0.6rem", borderColor: `${accent}55`, color: accent }}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" display="block">
        Booking
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ fontFamily: "monospace", fontSize: compact ? "0.72rem" : "0.8rem", mb: 0.75 }}>
        {booking}
      </Typography>
      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
        <Chip label={container ?? "Sin container"} size="small" sx={{ height: 22, fontFamily: "monospace", fontWeight: 700 }} />
        <Chip label={equipmentCode ?? "Sin equipment"} size="small" variant="outlined" sx={{ height: 22, fontFamily: "monospace" }} />
      </Stack>
      {equipmentId ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.65 }}>
          Equipment #{equipmentId}
        </Typography>
      ) : null}
    </Box>
  );
}

export function EquipmentLinksSummary({
  links,
  currentBookingId,
  context,
  title = "Links por equipment",
  emptyMessage = "Sin links activos por equipment; no se infiere conciliación desde LinkedBookingId.",
  compact = false,
}: EquipmentLinksSummaryProps) {
  if (!links.length) {
    return (
      <Alert severity="info" sx={{ py: compact ? 0.5 : 0.75, borderRadius: 1.5 }}>
        <Typography variant="caption" fontWeight={700} display="block">
          Sin links activos por equipment
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {emptyMessage}
        </Typography>
      </Alert>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1 }}>
        <ViewInArOutlined sx={{ fontSize: 16, color: "#0f766e" }} />
        <Typography variant="subtitle2" fontWeight={800} sx={{ fontSize: compact ? "0.78rem" : "0.9rem" }}>
          {title}
        </Typography>
        <Chip label={links.length} size="small" color="success" sx={{ height: 18, fontSize: "0.65rem" }} />
      </Stack>

      <Stack spacing={compact ? 0.75 : 1}>
        {links.map((rawLink, index) => {
          const link = normalizeLink(rawLink, context);
          const currentIsRepoDoc = currentBookingId != null && currentBookingId === link.repoDocBookingId;
          const currentIsNaviera = currentBookingId != null && currentBookingId === link.navieraBookingId;
          const showPerspective = currentIsRepoDoc || currentIsNaviera;
          const leftIsRepoDoc = showPerspective ? currentIsRepoDoc : true;

          const left = leftIsRepoDoc
            ? {
                label: showPerspective ? "Actual" : "REPO_DOC",
                source: "REPO_DOC" as const,
                booking: bookingLabel(link.repoDocBookingReference, link.repoDocBookingId),
                container: link.repoDocContainerId,
                equipmentCode: link.repoDocEquipmentCode,
                equipmentId: link.repoDocBookingEquipmentId,
                accent: "#0369a1",
              }
            : {
                label: "Actual",
                source: "NAVIERA" as const,
                booking: bookingLabel(link.navieraBookingReference, link.navieraBookingId),
                container: link.navieraContainerId,
                equipmentCode: link.navieraEquipmentCode,
                equipmentId: link.navieraBookingEquipmentId,
                accent: "#15803d",
              };

          const right = leftIsRepoDoc
            ? {
                label: showPerspective ? "Contraparte" : "NAVIERA",
                source: "NAVIERA" as const,
                booking: bookingLabel(link.navieraBookingReference, link.navieraBookingId),
                container: link.navieraContainerId,
                equipmentCode: link.navieraEquipmentCode,
                equipmentId: link.navieraBookingEquipmentId,
                accent: "#15803d",
              }
            : {
                label: "Contraparte",
                source: "REPO_DOC" as const,
                booking: bookingLabel(link.repoDocBookingReference, link.repoDocBookingId),
                container: link.repoDocContainerId,
                equipmentCode: link.repoDocEquipmentCode,
                equipmentId: link.repoDocBookingEquipmentId,
                accent: "#0369a1",
              };

          return (
            <Box key={link.id ?? `${left.equipmentId}-${right.equipmentId}-${index}`}>
              {index > 0 ? <Divider sx={{ mb: compact ? 0.75 : 1 }} /> : null}
              <Stack direction={{ xs: "column", sm: "row" }} alignItems="stretch" spacing={1}>
                <EquipmentSideCard {...left} compact={compact} />
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", color: "text.disabled" }}>
                  <ArrowForwardOutlined fontSize="small" />
                </Box>
                <EquipmentSideCard {...right} compact={compact} />
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}
